/**
 * templatesLLM — LLMPort adapter using deterministic templates.
 * No actual LLM call. Used in dev / demo / offline mode.
 *
 * Real LLM adapters (Claude, Gemini, OpenAI) implement the same port.
 */
import { createPlay } from "../../domain/entities";

const TEMPLATES = {
  recovery: [
    {
      title: "Llamada ejecutiva con propuesta de reactivación",
      channel: "call",
      body:
        "Hola {name}, notamos que {company} no ha renovado su servicio en los últimos {months} meses. " +
        "Nos gustaría entender qué ocurrió y ofrecerle condiciones preferenciales para retomar la " +
        "relación comercial. ¿Podemos agendar 15 minutos esta semana?",
      lift: 0.28,
    },
    {
      title: "Secuencia consultiva con caso de éxito",
      channel: "email",
      body:
        "Buen día {name}, adjunto un caso reciente del sector {segment}. Nos gustaría retomar la " +
        "conversación con una propuesta actualizada a las necesidades actuales de {company}. " +
        "¿Le parece si conversamos el jueves?",
      lift: 0.16,
    },
    {
      title: "Mensaje directo por WhatsApp",
      channel: "whatsapp",
      body:
        "Hola {name}, estuve revisando la relación comercial con {company} y creo que hay una " +
        "oportunidad clara de recuperar impulso. ¿Tienes 10 min hoy o mañana?",
      lift: 0.21,
    },
  ],
  renewal: [
    {
      title: "Aviso de renovación próxima",
      channel: "whatsapp",
      body:
        "Hola {name}, tu renovación con {company} vence en {days} días. Quiero asegurarme de que " +
        "sigas teniendo las mejores condiciones — ¿lo revisamos esta semana?",
      lift: 0.24,
    },
    {
      title: "Llamada de renovación con revisión de cuenta",
      channel: "call",
      body:
        "Hola {name}, antes de que venza tu renovación en {days} días me gustaría hacer una " +
        "revisión rápida de cómo te ha ido con {company} y confirmar los términos para el siguiente periodo.",
      lift: 0.19,
    },
    {
      title: "Correo de renovación con condiciones",
      channel: "email",
      body:
        "Buen día {name}, tu renovación está programada en {days} días. Adjunto las condiciones " +
        "vigentes para {company} — dime si quieres que ajustemos algo antes de confirmar.",
      lift: 0.15,
    },
  ],
  // Sprint 12 — corregido tras revisión de calidad sobre un envío real:
  // (1) nunca usar company_name (fuga de nombres de laboratorio/demo — un
  //     ejecutivo tampoco le repite a su cliente el nombre de su propia
  //     empresa por WhatsApp, así que quitarlo también suena más natural);
  // (2) nunca afirmar que "vimos cómo usa el servicio" — no hay esa
  //     evidencia; {current} en cambio es un HECHO real (lo que ya tiene
  //     contratado), no una inferencia de comportamiento;
  // (3) cierre con más gancho que "¿te comparto los detalles?".
  cross_sell: [
    {
      title: "Oferta de producto adicional por WhatsApp",
      channel: "whatsapp",
      body:
        "Hola {name}, como ya tienes {current} con nosotros, quería contarte sobre {product} — " +
        "creo que te puede servir. ¿Te cuento cómo funciona?",
      lift: 0.2,
    },
    {
      title: "Llamada de expansión de cuenta",
      channel: "call",
      body:
        "Hola {name}, quiero platicarte de {product} — complementa bien lo que ya tienes con " +
        "{current}. ¿Tienes 15 minutos esta semana?",
      lift: 0.17,
    },
    {
      title: "Correo con propuesta de producto adicional",
      channel: "email",
      body:
        "Buen día {name}, como cliente de {current}, quería comentarte sobre {product} — " +
        "podría ser un buen siguiente paso para ti. Dime si lo revisamos.",
      lift: 0.14,
    },
  ],
};

function pickTemplate(engineId, index) {
  const allForEngine = TEMPLATES[engineId] || TEMPLATES.recovery;
  // Solo WhatsApp está conectado como canal real (Sprint 11 noche) — sugerir
  // un play de "call" o "email" confundía al operador, porque el botón
  // Enviar siempre manda por WhatsApp sin importar qué canal diga la
  // etiqueta (hallazgo de la prueba real con LAB-REN-004: la play decía
  // "email" pero el envío real fue WhatsApp). Cuando se conecte otro canal,
  // esto deja de filtrar y vuelve a variar.
  const whatsappOnly = allForEngine.filter((t) => t.channel === "whatsapp");
  const pool = whatsappOnly.length ? whatsappOnly : allForEngine;
  return pool[index % pool.length];
}

function fillVars(body, actor) {
  const first = actor.contactName?.split(" ")[0] || "hola";
  const months = actor.verticalAttributes?.monthsInactive ?? 6;
  const days = actor.verticalAttributes?.daysUntilRenewal ?? 30;
  const product = actor.verticalAttributes?.crossSellProduct || "un producto adicional";
  // Solo para cross_sell — un HECHO conocido (lo que ya tiene contratado),
  // nunca una inferencia de uso/comportamiento. La elegibilidad de
  // Venta Cruzada ya exige currentProduct presente; el fallback es solo
  // defensivo por si este template se invoca fuera de ese camino.
  const current = actor.verticalAttributes?.currentProduct || "nuestro servicio";
  return body
    .replace(/{name}/g, first)
    .replace(/{company}/g, actor.companyName || "")
    .replace(/{months}/g, String(months))
    .replace(/{days}/g, String(days))
    .replace(/{product}/g, product)
    .replace(/{current}/g, current)
    .replace(/{segment}/g, actor.segment || "");
}

// Explicación comercial de por qué este mensaje, por motor — nunca lenguaje
// técnico (nada de "LLM", "biblioteca de plantillas", "MCP"). Lo que ve el
// operador es la razón de negocio, no cómo se generó el texto.
const RATIONALE_BY_ENGINE = {
  recovery:
    "Los clientes inactivos que reciben este mensaje dentro de su primera semana " +
    "de seguimiento responden con más frecuencia que si se espera más tiempo.",
  renewal:
    "Contactar antes de la fecha de vencimiento, con condiciones claras, es lo que " +
    "más sube la probabilidad de que la renovación se confirme sin fricción.",
  cross_sell:
    "Los clientes con uso activo del servicio son los que mejor responden a una " +
    "propuesta de producto adicional — están viendo valor ahora mismo.",
};

/** @returns {import('../../domain/ports').LLMPort} */
export function createTemplatesLLM() {
  let counter = 0;

  return {
    async status() {
      return { vendor: "templates", model: "deterministic-v1", status: "ready" };
    },

    async generatePlay({ actor, opportunity, engineId }) {
      const t = pickTemplate(engineId, counter++);
      return createPlay({
        opportunityId: opportunity.id,
        channel: t.channel,
        title: t.title,
        rationale: RATIONALE_BY_ENGINE[engineId] || RATIONALE_BY_ENGINE.recovery,
        suggestedBody: fillVars(t.body, actor),
        suggestedFor: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
        expectedLiftPct: t.lift,
        generatedBy: "template",
      });
    },

    async regeneratePlay({ play }) {
      // Templates don't rewrite; return a new template pick.
      return createPlay({
        ...play,
        title: play.title,
        suggestedBody: play.suggestedBody,
        generatedAt: new Date().toISOString(),
      });
    },
  };
}
